/**
 * EVIDENCE PANEL
 *
 * Tabbed panel (Details / Changes / CI / Risk) with action buttons.
 * Redesigned with Linear-style flat meta fields and Colony design tokens.
 */

import { useState, useRef, useEffect } from "react";
import { type Run, type RiskLevel } from "@/stores/reviewStore";
import { useReviewStore } from "@/stores/reviewStore";
import { useWorldStore } from "@/stores/worldStore";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { AIActivityLog } from "./AIActivityLog";
import {
  GitMerge,
  GitPullRequest,
  GitBranch,
  CheckCircle,
  XCircle,
  Loader2,
  Shield,
  AlertTriangle,
  ExternalLink,
  FileCode,
  FileText,
  Terminal,
  SquareTerminal,
  RefreshCw,
  Eye,
  ChevronDown,
  ChevronRight,
  Bot,
  AlertCircle,
  Clock,
  Sparkles,
  Github,
  Trash2,
  RotateCcw,
  UserCheck,
  Play,
  Check,
  Minus,
  SkipForward,
} from "lucide-react";
import { useAiChatStore } from "@/stores/aiChatStore";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { isTauri } from "@/hooks/usePlatform";
import { getMergeErrorMessage } from "@/lib/mergeErrors";
import { cn } from "@/lib/utils";
import { mapEvidenceToChecks } from "@/lib/data/evidence";

async function tauriInvoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<T>(cmd, args);
}

// ── Config ──────────────────────────────────────────────────────────

const riskConfig: Record<RiskLevel, { label: string; short: string; colorVar: string; bgClass: string }> = {
  low:      { label: "Low Risk",      short: "Low",      colorVar: "var(--colony-green)", bgClass: "bg-[rgba(60,203,127,0.1)]" },
  medium:   { label: "Medium Risk",   short: "Medium",   colorVar: "var(--colony-amber)", bgClass: "bg-[rgba(229,167,59,0.1)]" },
  high:     { label: "High Risk",     short: "High",     colorVar: "var(--colony-red)",   bgClass: "bg-[rgba(229,83,75,0.1)]" },
  critical: { label: "Critical Risk", short: "Critical", colorVar: "var(--colony-red)",   bgClass: "bg-[rgba(229,83,75,0.1)]" },
};

function computeSystemConfidence(run: Run): { level: "high" | "medium" | "low"; reasons: string[] } {
  const reasons: string[] = [];
  let score = 100;

  const hasCiChecks = run.ciStatus.length > 0;
  const allPassed = hasCiChecks && run.ciStatus.every(c => c.status === "passed");
  const someFailed = run.ciStatus.some(c => c.status === "failed");
  const someRunning = run.ciStatus.some(c => c.status === "running" || c.status === "pending");

  if (someFailed) { score -= 40; reasons.push("CI checks failing"); }
  else if (someRunning) { score -= 20; reasons.push("CI still running"); }
  else if (allPassed) { reasons.push("All CI passed"); }

  if (run.riskLevel === "high" || run.riskLevel === "critical") score -= 20;
  else if (run.riskLevel === "low") score += 10;

  if (run.protectedFilesTouched.length > 0) { score -= 15; reasons.push("Protected files touched"); }

  const totalChanges = run.filesChanged.reduce((sum, f) => sum + f.additions + f.deletions, 0);
  if (totalChanges < 100) reasons.push("Small changeset");
  else if (totalChanges > 500) { score -= 10; reasons.push("Large changeset"); }

  if (run.testResults) {
    if (run.testResults.failed > 0) { score -= 30; reasons.push(`${run.testResults.failed} tests failing`); }
    else reasons.push("Tests passing");
  }

  if (score >= 80) return { level: "high", reasons };
  if (score >= 50) return { level: "medium", reasons };
  return { level: "low", reasons };
}

const confidenceColorVar: Record<string, string> = {
  high: "var(--colony-green)",
  medium: "var(--colony-amber)",
  low: "var(--colony-red)",
};

type VerificationState = "not_run" | "running" | "failed" | "passed";

function getVerificationState(run: Run): VerificationState {
  const hasCiChecks = run.ciStatus.length > 0;
  const someCIFailed = run.ciStatus.some(ci => ci.status === "failed");
  const someCIRunning = run.ciStatus.some(ci => ci.status === "running" || ci.status === "pending");
  const allCIPassed = hasCiChecks && run.ciStatus.every(ci => ci.status === "passed" || ci.status === "skipped");
  const hasTestResults = Boolean(run.testResults && run.testResults.total > 0);
  const testsFailed = hasTestResults && (run.testResults?.failed ?? 0) > 0;
  const testsPassed = hasTestResults && (run.testResults?.failed ?? 0) === 0;

  if (someCIFailed || testsFailed) return "failed";
  if (someCIRunning) return "running";
  if (allCIPassed) return "passed";
  if (!hasCiChecks && testsPassed) return "passed";
  return "not_run";
}

function hasMergeArtifacts(run: Run): boolean {
  if (run.filesChanged.length > 0 || Boolean(run.prUrl) || Boolean(run.prNumber)) return true;
  // Task-backed runs with a completed workflow may have a PR in the workflow run
  // node results (backend resolves it) even if the frontend metadata doesn't have it yet.
  if (run.source === "task" && run.taskWorkflowStatus === "completed") return true;
  return false;
}

function getMergeDisabledReason(run: Run, verificationState: VerificationState): string | undefined {
  if (run.hasMergeConflict) return "Resolve merge conflicts before merging";
  if (!hasMergeArtifacts(run)) return "No code changes or pull request available yet";
  if (verificationState === "failed") return "Verification failed — fix issues before merging";
  // "not_run" and "running" don't block merge — projects without CI should still be mergeable
  return undefined;
}

// ── Meta Field (Linear-style flat) ──────────────────────────────────

function MetaField({ label, children, onClick }: { label: string; children: React.ReactNode; onClick?: () => void }) {
  return (
    <div
      className={cn("flex items-center h-7 group/field", onClick && "cursor-pointer")}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
    >
      <span className="w-[80px] shrink-0 text-[11px] text-[var(--colony-text-tertiary)]">{label}</span>
      <div className="flex-1 min-w-0 flex items-center gap-1.5 text-[13px] text-[var(--colony-text-primary)] rounded-[4px] px-1.5 -mx-1.5 h-6 group-hover/field:bg-[rgba(255,255,255,0.03)] transition-colors">
        {children}
      </div>
    </div>
  );
}

// ── Tab Types ───────────────────────────────────────────────────────

type EvidenceTab = "details" | "context" | "changes" | "ci" | "risk";

// ── Details Tab ─────────────────────────────────────────────────────

function DetailsTab({ run, onTabChange }: { run: Run; onTabChange?: (tab: EvidenceTab) => void }) {
  const totalAdditions = run.filesChanged.reduce((sum, f) => sum + f.additions, 0);
  const totalDeletions = run.filesChanged.reduce((sum, f) => sum + f.deletions, 0);
  const systemConfidence = computeSystemConfidence(run);
  const risk = riskConfig[run.riskLevel];
  const agents = run.agents || (run.agentName ? [{ name: run.agentName, role: "primary" as const, model: "claude" as const }] : []);
  const verificationState = getVerificationState(run);

  const statusLabel: Record<string, string> = {
    running: "Running", blocked: "Blocked", needs_review: "Needs Review",
    ready: "Ready", merged: "Merged", failed: "Failed",
  };

  const statusColor: Record<string, string> = {
    running: "var(--colony-blue)", blocked: "var(--colony-red)", needs_review: "var(--colony-amber)",
    ready: "var(--colony-green)", merged: "var(--colony-violet)", failed: "var(--colony-red)",
  };

  const timeSince = (date: string) => {
    const now = new Date();
    const then = new Date(date);
    const diff = Math.floor((now.getTime() - then.getTime()) / 1000 / 60);
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return new Date(date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div className="p-4 space-y-0.5">
      {/* Decision Summary inline */}
      {run.decisionSummary ? (
        <div className="mb-3 space-y-2">
          <div>
            <div className="text-[11px] font-medium text-[var(--colony-text-tertiary)] uppercase tracking-[0.06em] mb-1">Issue</div>
            <p className="text-[13px] text-[var(--colony-text-primary)] leading-relaxed">{run.decisionSummary.issue}</p>
          </div>
          <div>
            <div className="text-[11px] font-medium text-[var(--colony-text-tertiary)] uppercase tracking-[0.06em] mb-1">Root Cause</div>
            <p className="text-[13px] text-[var(--colony-text-primary)] leading-relaxed">{run.decisionSummary.rootCause}</p>
          </div>
          <div>
            <div className="text-[11px] font-medium text-[var(--colony-text-tertiary)] uppercase tracking-[0.06em] mb-1">Fix Strategy</div>
            <p className="text-[13px] text-[var(--colony-text-primary)] leading-relaxed">{run.decisionSummary.fixStrategy}</p>
          </div>
          <div className="h-px bg-[var(--colony-border)] !my-3" />
        </div>
      ) : (
        <div className="mb-3">
          <p className="text-[13px] text-[var(--colony-text-primary)] leading-relaxed">{run.title}</p>
          {run.description && run.description !== run.title && (
            <p className="text-[12px] text-[var(--colony-text-secondary)] leading-relaxed mt-1.5 whitespace-pre-wrap line-clamp-5">{run.description}</p>
          )}
          {run.rationale && (
            <p className="text-[12px] text-[var(--colony-text-secondary)] leading-relaxed mt-1 line-clamp-3">{run.rationale}</p>
          )}
          {((run.description && run.description.length > 300) || (run.rationale && run.rationale.length > 200)) && onTabChange && (
            <button
              onClick={() => onTabChange("context")}
              className="text-[11px] text-[var(--colony-brand)] hover:underline mt-1 cursor-pointer"
            >
              Read more
            </button>
          )}
          <div className="h-px bg-[var(--colony-border)] !my-3" />
        </div>
      )}

      {/* Meta fields */}
      <MetaField label="Status">
        <span className="text-[13px] font-medium" style={{ color: statusColor[run.status] }}>
          {run.status === "running" && <Loader2 className="w-3 h-3 animate-spin inline mr-1" style={{ color: statusColor[run.status] }} />}
          {statusLabel[run.status] || run.status}
        </span>
      </MetaField>

      {agents.length > 0 && (
        <MetaField label="Agent">
          <Bot className="w-3 h-3 text-[var(--colony-text-tertiary)]" />
          <span className="text-[13px]">
            {agents.slice(0, 2).map(a => a.name).join(", ")}
            {agents.length > 2 && <span className="text-[var(--colony-text-tertiary)]"> +{agents.length - 2}</span>}
          </span>
        </MetaField>
      )}

      <MetaField label="Project">
        <span className="text-[13px] text-[var(--colony-brand)] hover:underline cursor-pointer">{run.projectName}</span>
      </MetaField>

      <MetaField label="Type">
        <span className={cn(
          "inline-flex items-center h-[18px] px-1.5 rounded-[3px] text-[10px] font-medium border",
          run.issueType === "bug" ? "border-[rgba(229,83,75,0.2)] bg-[rgba(229,83,75,0.1)] text-[var(--colony-red)]"
            : run.issueType === "hotfix" ? "border-[rgba(229,83,75,0.2)] bg-[rgba(229,83,75,0.1)] text-[var(--colony-red)]"
            : run.issueType === "refactor" ? "border-[rgba(136,108,230,0.2)] bg-[rgba(136,108,230,0.1)] text-[var(--colony-violet)]"
            : "border-[rgba(83,155,245,0.2)] bg-[rgba(83,155,245,0.1)] text-[var(--colony-blue)]"
        )}>
          {run.issueType === "hotfix" ? "Hotfix" : run.issueType === "bug" ? "Bug" : run.issueType === "refactor" ? "Refactor" : "Feature"}
        </span>
      </MetaField>

      {run.priority && (
        <MetaField label="Priority">
          <span className={cn(
            "inline-flex items-center h-[18px] px-1.5 rounded-[3px] text-[10px] font-medium border",
            run.priority === "critical" || run.priority === "high"
              ? "border-[rgba(229,83,75,0.2)] bg-[rgba(229,83,75,0.1)] text-[var(--colony-red)]"
              : run.priority === "medium"
              ? "border-[rgba(229,167,59,0.2)] bg-[rgba(229,167,59,0.1)] text-[var(--colony-amber)]"
              : "border-[var(--colony-border)] bg-[var(--colony-surface-elevated)] text-[var(--colony-text-secondary)]"
          )}>
            {run.priority.charAt(0).toUpperCase() + run.priority.slice(1)}
          </span>
        </MetaField>
      )}

      {run.impact && (
        <MetaField label="Impact">
          <span className="text-[13px] text-[var(--colony-text-secondary)] capitalize">{run.impact}</span>
        </MetaField>
      )}

      {run.workflowName && (
        <MetaField label="Workflow">
          <Sparkles size={12} strokeWidth={1.5} className="text-[var(--colony-text-tertiary)]" />
          <span className="text-[13px] text-[var(--colony-text-secondary)]">{run.workflowName}</span>
        </MetaField>
      )}

      {run.branch && (
        <MetaField label="Branch">
          <GitBranch size={12} strokeWidth={1.5} className="text-[var(--colony-text-tertiary)] shrink-0" />
          <span className="text-[13px] font-mono text-[var(--colony-text-secondary)] truncate">{run.branch}</span>
        </MetaField>
      )}

      <MetaField label="Risk">
        <span className="text-[13px] font-medium" style={{ color: risk.colorVar }}>{risk.short}</span>
      </MetaField>

      <MetaField label="Confidence">
        <span className="text-[13px] font-medium capitalize" style={{ color: confidenceColorVar[systemConfidence.level] }}>
          {systemConfidence.level}
        </span>
      </MetaField>

      {run.scopeImpact && run.scopeImpact.subsystemsAffected.length > 0 && (
        <MetaField label="Scope">
          <div className="flex items-center gap-1 flex-wrap">
            {run.scopeImpact.subsystemsAffected.map((s, i) => (
              <span key={i} className="inline-flex items-center h-[18px] px-1.5 rounded-[3px] text-[10px] font-medium border border-[var(--colony-border)] bg-[var(--colony-surface-elevated)] text-[var(--colony-text-secondary)]">
                {s}
              </span>
            ))}
          </div>
        </MetaField>
      )}

      <MetaField label="Created">
        <span className="text-[13px] text-[var(--colony-text-secondary)]">{timeSince(run.startedAt)}</span>
      </MetaField>

      {/* Divider */}
      <div className="h-px bg-[var(--colony-border)] !my-3" />

      {/* Pipeline compact */}
      <div className="space-y-2">
        <span className="text-[11px] font-medium text-[var(--colony-text-tertiary)] uppercase tracking-[0.06em]">Pipeline</span>
        <div className="flex items-center gap-2">
          {verificationState === "failed" ? (
            <AlertTriangle size={12} strokeWidth={1.5} style={{ color: "var(--colony-red)" }} />
          ) : verificationState === "passed" ? (
            <CheckCircle size={12} strokeWidth={1.5} style={{ color: "var(--colony-green)" }} />
          ) : verificationState === "running" ? (
            <Loader2 size={12} strokeWidth={1.5} className="animate-spin" style={{ color: "var(--colony-blue)" }} />
          ) : (
            <Clock size={12} strokeWidth={1.5} className="text-[var(--colony-text-tertiary)]" />
          )}
          <span className="text-[12px] font-medium" style={{
            color: verificationState === "failed" ? "var(--colony-red)"
              : verificationState === "passed" ? "var(--colony-green)"
              : verificationState === "running" ? "var(--colony-blue)"
              : "var(--colony-text-tertiary)"
          }}>
            {verificationState === "not_run" ? "Not run yet"
              : verificationState === "running" ? "Running..."
              : verificationState === "passed" ? "All checks passed"
              : "Checks failing"}
          </span>
          {run.ciStatus.length > 0 && (
            <span className="text-[10px] text-[var(--colony-text-tertiary)] ml-auto">
              {run.ciStatus.filter(c => c.status === "passed").length}/{run.ciStatus.length}
            </span>
          )}
        </div>
        {run.ciStatus.length > 0 && (
          <div className="flex items-center gap-1">
            {run.ciStatus.map((ci, i) => (
              <div
                key={i}
                className="h-[4px] flex-1 rounded-full"
                style={{
                  backgroundColor: ci.status === "passed" ? "var(--colony-green)"
                    : ci.status === "failed" ? "var(--colony-red)"
                    : ci.status === "running" ? "var(--colony-blue)"
                    : "var(--colony-text-tertiary)"
                }}
                title={`${ci.name}: ${ci.status}${ci.duration ? ` (${ci.duration})` : ""}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="h-px bg-[var(--colony-border)] !my-3" />

      {/* Changes summary */}
      <div className="space-y-2">
        <span className="text-[11px] font-medium text-[var(--colony-text-tertiary)] uppercase tracking-[0.06em]">Changes</span>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <FileText size={12} strokeWidth={1.5} className="text-[var(--colony-text-tertiary)]" />
            <span className="text-[12px] text-[var(--colony-text-secondary)]">{run.filesChanged.length} files</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[12px] font-mono" style={{ color: "var(--colony-green)" }}>+{totalAdditions}</span>
            <span className="text-[12px] text-[var(--colony-text-tertiary)]">/</span>
            <span className="text-[12px] font-mono" style={{ color: "var(--colony-red)" }}>-{totalDeletions}</span>
          </div>
        </div>
      </div>

      {/* Protected files warning */}
      {run.protectedFilesTouched.length > 0 && (
        <>
          <div className="h-px bg-[var(--colony-border)] !my-3" />
          <div className="flex items-center gap-2 text-[12px] font-medium" style={{ color: "var(--colony-amber)" }}>
            <Shield size={12} strokeWidth={1.5} />
            <span>{run.protectedFilesTouched.length} protected file{run.protectedFilesTouched.length > 1 ? "s" : ""} touched</span>
          </div>
        </>
      )}

      {/* External links */}
      {(run.previewUrl || run.prUrl) && (
        <>
          <div className="h-px bg-[var(--colony-border)] !my-3" />
          <div className="space-y-1.5">
            {run.previewUrl && (
              <a href={run.previewUrl} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 text-[12px] text-[var(--colony-brand)] hover:underline">
                <Eye size={12} strokeWidth={1.5} />Preview
                <ExternalLink size={10} className="ml-auto opacity-50" />
              </a>
            )}
            {run.prUrl && (
              <a href={run.prUrl} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 text-[12px] text-[var(--colony-text-secondary)] hover:text-[var(--colony-text-primary)]">
                <GitPullRequest size={12} strokeWidth={1.5} />Pull Request
                <ExternalLink size={10} className="ml-auto opacity-50" />
              </a>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Context Tab ─────────────────────────────────────────────────────

function ContextSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] font-medium text-[var(--colony-text-tertiary)] uppercase tracking-[0.06em] mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function ContextTab({ run }: { run: Run }) {
  const si = run.startInput;
  const hasStartInput = si && typeof si === "object" && Object.keys(si).length > 0;

  // Gather issue-specific fields from startInput
  const bugFields = si ? {
    stepsToReproduce: si.steps_to_reproduce ?? si.stepsToReproduce,
    expectedBehavior: si.expected_behavior ?? si.expectedBehavior,
    actualBehavior: si.actual_behavior ?? si.actualBehavior,
    severity: si.severity,
  } : {};

  const featureFields = si ? {
    userStory: si.user_story ?? si.userStory,
    acceptanceCriteria: si.acceptance_criteria ?? si.acceptanceCriteria,
    scope: si.scope,
    constraints: si.constraints,
  } : {};

  const refactorFields = si ? {
    motivation: si.motivation,
    targetFiles: si.target_files ?? si.targetFiles,
    approach: si.approach,
    riskLevel: si.risk_level ?? si.riskLevel,
  } : {};

  const hasBugFields = run.issueType === "bug" && Object.values(bugFields).some(Boolean);
  const hasFeatureFields = run.issueType === "feature" && Object.values(featureFields).some(Boolean);
  const hasRefactorFields = run.issueType === "refactor" && Object.values(refactorFields).some(Boolean);

  return (
    <div className="p-4 space-y-4">
      {/* Description / Prompt */}
      {(run.description || (si?.description && si.description !== run.description)) && (
        <ContextSection label="Description">
          {run.description && (
            <p className="text-[13px] text-[var(--colony-text-primary)] leading-relaxed whitespace-pre-wrap">{run.description}</p>
          )}
          {si?.description && si.description !== run.description && si.description !== run.title && (
            <p className="text-[12px] text-[var(--colony-text-secondary)] leading-relaxed mt-1.5 whitespace-pre-wrap">{si.description}</p>
          )}
        </ContextSection>
      )}

      {/* Bug-specific fields */}
      {hasBugFields && (
        <>
          <div className="h-px bg-[var(--colony-border)]" />
          <div className="space-y-3">
            {bugFields.stepsToReproduce && (
              <ContextSection label="Steps to Reproduce">
                <p className="text-[12px] text-[var(--colony-text-secondary)] leading-relaxed whitespace-pre-wrap">{bugFields.stepsToReproduce}</p>
              </ContextSection>
            )}
            {bugFields.expectedBehavior && (
              <ContextSection label="Expected Behavior">
                <p className="text-[12px] text-[var(--colony-text-secondary)] leading-relaxed whitespace-pre-wrap">{bugFields.expectedBehavior}</p>
              </ContextSection>
            )}
            {bugFields.actualBehavior && (
              <ContextSection label="Actual Behavior">
                <p className="text-[12px] text-[var(--colony-text-secondary)] leading-relaxed whitespace-pre-wrap">{bugFields.actualBehavior}</p>
              </ContextSection>
            )}
            {bugFields.severity && (
              <ContextSection label="Severity">
                <span className="text-[12px] text-[var(--colony-text-secondary)] capitalize">{bugFields.severity}</span>
              </ContextSection>
            )}
          </div>
        </>
      )}

      {/* Feature-specific fields */}
      {hasFeatureFields && (
        <>
          <div className="h-px bg-[var(--colony-border)]" />
          <div className="space-y-3">
            {featureFields.userStory && (
              <ContextSection label="User Story">
                <p className="text-[12px] text-[var(--colony-text-secondary)] leading-relaxed whitespace-pre-wrap">{featureFields.userStory}</p>
              </ContextSection>
            )}
            {featureFields.acceptanceCriteria && (
              <ContextSection label="Acceptance Criteria">
                <p className="text-[12px] text-[var(--colony-text-secondary)] leading-relaxed whitespace-pre-wrap">{featureFields.acceptanceCriteria}</p>
              </ContextSection>
            )}
            {featureFields.scope && (
              <ContextSection label="Scope">
                <p className="text-[12px] text-[var(--colony-text-secondary)] leading-relaxed whitespace-pre-wrap">{featureFields.scope}</p>
              </ContextSection>
            )}
            {featureFields.constraints && (
              <ContextSection label="Constraints">
                <p className="text-[12px] text-[var(--colony-text-secondary)] leading-relaxed whitespace-pre-wrap">{featureFields.constraints}</p>
              </ContextSection>
            )}
          </div>
        </>
      )}

      {/* Refactor-specific fields */}
      {hasRefactorFields && (
        <>
          <div className="h-px bg-[var(--colony-border)]" />
          <div className="space-y-3">
            {refactorFields.motivation && (
              <ContextSection label="Motivation">
                <p className="text-[12px] text-[var(--colony-text-secondary)] leading-relaxed whitespace-pre-wrap">{refactorFields.motivation}</p>
              </ContextSection>
            )}
            {refactorFields.targetFiles && (
              <ContextSection label="Target Files">
                <p className="text-[12px] text-[var(--colony-text-secondary)] leading-relaxed font-mono whitespace-pre-wrap">{Array.isArray(refactorFields.targetFiles) ? refactorFields.targetFiles.join("\n") : refactorFields.targetFiles}</p>
              </ContextSection>
            )}
            {refactorFields.approach && (
              <ContextSection label="Approach">
                <p className="text-[12px] text-[var(--colony-text-secondary)] leading-relaxed whitespace-pre-wrap">{refactorFields.approach}</p>
              </ContextSection>
            )}
            {refactorFields.riskLevel && (
              <ContextSection label="Risk Level">
                <span className="text-[12px] text-[var(--colony-text-secondary)] capitalize">{refactorFields.riskLevel}</span>
              </ContextSection>
            )}
          </div>
        </>
      )}

      {/* Meta badges */}
      {(run.priority || run.complexity || run.impact) && (
        <>
          <div className="h-px bg-[var(--colony-border)]" />
          <div className="flex items-center gap-2 flex-wrap">
            {run.priority && (
              <span className={cn(
                "inline-flex items-center h-[20px] px-2 rounded-[4px] text-[10px] font-medium",
                run.priority === "critical" || run.priority === "high"
                  ? "bg-[rgba(229,83,75,0.1)] text-[var(--colony-red)]"
                  : run.priority === "medium"
                  ? "bg-[rgba(229,167,59,0.1)] text-[var(--colony-amber)]"
                  : "bg-[var(--colony-surface-elevated)] text-[var(--colony-text-secondary)]"
              )}>
                Priority: {run.priority}
              </span>
            )}
            {run.complexity && (
              <span className="inline-flex items-center h-[20px] px-2 rounded-[4px] text-[10px] font-medium bg-[var(--colony-surface-elevated)] text-[var(--colony-text-secondary)]">
                Complexity: {run.complexity}
              </span>
            )}
            {run.impact && (
              <span className="inline-flex items-center h-[20px] px-2 rounded-[4px] text-[10px] font-medium bg-[rgba(83,155,245,0.1)] text-[var(--colony-blue)]">
                Impact: {run.impact}
              </span>
            )}
          </div>
        </>
      )}

      {/* Workflow */}
      {run.workflowName && (
        <>
          <div className="h-px bg-[var(--colony-border)]" />
          <div className="flex items-center gap-2">
            <Sparkles size={13} strokeWidth={1.5} className="text-[var(--colony-text-tertiary)]" />
            <span className="text-[12px] text-[var(--colony-text-secondary)]">Workflow: {run.workflowName}</span>
          </div>
        </>
      )}

      {/* Empty state */}
      {!run.description && !hasStartInput && !run.priority && !run.complexity && !run.impact && !run.workflowName && (
        <div className="text-center py-8 text-[12px] text-[var(--colony-text-tertiary)]">No task context available</div>
      )}
    </div>
  );
}

// ── Changes Tab ─────────────────────────────────────────────────────

function ChangesTab({ run }: { run: Run }) {
  const totalAdded = run.filesChanged.reduce((s, f) => s + f.additions, 0);
  const totalDeleted = run.filesChanged.reduce((s, f) => s + f.deletions, 0);

  return (
    <div className="p-3 space-y-3">
      {/* Stats row */}
      <div className="flex items-center gap-3">
        <div className="flex-1 rounded-[6px] bg-[var(--colony-surface)] border border-[var(--colony-border)] px-2.5 py-2 text-center">
          <span className="text-[10px] text-[var(--colony-text-tertiary)] block">Files</span>
          <span className="text-[14px] font-medium font-mono text-[var(--colony-text-primary)]">{run.filesChanged.length}</span>
        </div>
        <div className="flex-1 rounded-[6px] bg-[var(--colony-surface)] border border-[var(--colony-border)] px-2.5 py-2 text-center">
          <span className="text-[10px] text-[var(--colony-text-tertiary)] block">Added</span>
          <span className="text-[14px] font-medium font-mono" style={{ color: "var(--colony-green)" }}>+{totalAdded}</span>
        </div>
        <div className="flex-1 rounded-[6px] bg-[var(--colony-surface)] border border-[var(--colony-border)] px-2.5 py-2 text-center">
          <span className="text-[10px] text-[var(--colony-text-tertiary)] block">Removed</span>
          <span className="text-[14px] font-medium font-mono" style={{ color: "var(--colony-red)" }}>-{totalDeleted}</span>
        </div>
      </div>

      {/* File list */}
      {run.filesChanged.length > 0 && (
        <div className="rounded-[8px] border border-[var(--colony-border)] bg-[var(--colony-surface)] overflow-hidden">
          <div className="px-3 py-2 border-b border-[var(--colony-border)]">
            <span className="text-[11px] font-medium text-[var(--colony-text-tertiary)] uppercase tracking-[0.05em]">Changed Files</span>
          </div>
          <div className="divide-y divide-[var(--colony-border)]">
            {run.filesChanged.map((file, i) => {
              const total = file.additions + file.deletions;
              const addPct = total > 0 ? (file.additions / total) * 100 : 0;
              return (
                <div key={i} className="flex items-center gap-2 px-3 py-2 hover:bg-[var(--colony-surface-hover)] transition-colors group">
                  <span className="w-4 text-center shrink-0">
                    {file.isProtected ? (
                      <Shield size={10} strokeWidth={1.5} style={{ color: "var(--colony-amber)" }} />
                    ) : (
                      <span className="text-[10px] font-mono font-medium" style={{ color: "var(--colony-amber)" }}>M</span>
                    )}
                  </span>
                  <span className="text-[12px] font-mono text-[var(--colony-text-secondary)] truncate flex-1 group-hover:text-[var(--colony-text-primary)] transition-colors">
                    {file.path}
                  </span>
                  <span className="text-[11px] font-mono shrink-0" style={{ color: "var(--colony-green)" }}>+{file.additions}</span>
                  <span className="text-[11px] font-mono shrink-0" style={{ color: "var(--colony-red)" }}>-{file.deletions}</span>
                  <div className="w-10 h-1 rounded-full bg-[var(--colony-surface-active)] overflow-hidden shrink-0">
                    <div className="h-full rounded-full" style={{ width: `${addPct}%`, backgroundColor: "var(--colony-green)" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {run.filesChanged.length === 0 && (
        <div className="text-center py-8 text-[12px] text-[var(--colony-text-tertiary)]">No files changed yet</div>
      )}
    </div>
  );
}

// ── Automated Checks (machine-run verification evidence) ────────────

function AutomatedCheckRow({ check }: { check: ReturnType<typeof mapEvidenceToChecks>[number] }) {
  const [showTail, setShowTail] = useState(false);
  const hasTail = !check.passed && check.outputTail.trim().length > 0;

  return (
    <div className="px-3 py-2">
      <div className="flex items-center gap-2.5">
        <span className="shrink-0">
          {check.passed
            ? <Check size={12} strokeWidth={2} style={{ color: "var(--colony-green)" }} />
            : <XCircle size={12} strokeWidth={1.5} style={{ color: "var(--colony-red)" }} />}
        </span>
        <span className="text-[12px] text-[var(--colony-text-primary)]">{check.name}</span>
        <span className="text-[11px] font-mono text-[var(--colony-text-tertiary)] truncate flex-1">{check.command}</span>
        {hasTail && (
          <button
            onClick={() => setShowTail(v => !v)}
            className="shrink-0 text-[var(--colony-text-tertiary)] hover:text-[var(--colony-text-secondary)] transition-colors cursor-pointer"
            title="Toggle output"
          >
            {showTail ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
        )}
      </div>
      {hasTail && showTail && (
        <pre className="mt-1.5 ml-[22px] p-2 rounded-[4px] bg-[var(--colony-surface-elevated)] text-[10px] font-mono text-[var(--colony-text-secondary)] whitespace-pre-wrap overflow-x-auto">
          {check.outputTail}
        </pre>
      )}
    </div>
  );
}

function AutomatedChecksSection({ run }: { run: Run }) {
  const checks = mapEvidenceToChecks(run.nodeResults ?? []);
  if (checks.length === 0) return null;
  const passCount = checks.filter(c => c.passed).length;

  return (
    <div className="rounded-[8px] border border-[var(--colony-border)] bg-[var(--colony-surface)] overflow-hidden">
      <div className="px-3 py-2 border-b border-[var(--colony-border)] flex items-center">
        <span className="text-[11px] font-medium text-[var(--colony-text-tertiary)] uppercase tracking-[0.05em]">Automated Checks</span>
        <span className="text-[10px] text-[var(--colony-text-tertiary)] ml-auto">{passCount}/{checks.length} passed</span>
      </div>
      <div className="divide-y divide-[var(--colony-border)]">
        {checks.map((check, i) => (
          <AutomatedCheckRow key={i} check={check} />
        ))}
      </div>
    </div>
  );
}

// ── CI Tab ──────────────────────────────────────────────────────────

function CITab({ run }: { run: Run }) {
  const verificationState = getVerificationState(run);
  const passCount = run.ciStatus.filter(c => c.status === "passed").length;
  const hasFail = run.ciStatus.some(c => c.status === "failed");
  const hasWarning = false; // No warning status in our data model

  const overallLabel = hasFail ? "Failing" : verificationState === "passed" ? "All checks passed" : verificationState === "running" ? "Running" : "Not run";
  const overallColor = hasFail ? "var(--colony-red)" : verificationState === "passed" ? "var(--colony-green)" : verificationState === "running" ? "var(--colony-blue)" : "var(--colony-text-tertiary)";
  const overallBg = hasFail ? "bg-[rgba(229,83,75,0.1)]" : verificationState === "passed" ? "bg-[rgba(60,203,127,0.1)]" : verificationState === "running" ? "bg-[rgba(83,155,245,0.1)]" : "bg-[var(--colony-surface)]";

  const ciStatusIcon = (status: string) => {
    switch (status) {
      case "passed": return <Check size={12} strokeWidth={2} style={{ color: "var(--colony-green)" }} />;
      case "failed": return <XCircle size={12} strokeWidth={1.5} style={{ color: "var(--colony-red)" }} />;
      case "running": return <div className="w-3 h-3 border-[1.5px] rounded-full animate-spin" style={{ borderColor: "var(--colony-blue)", borderTopColor: "transparent" }} />;
      case "pending": return <Minus size={12} strokeWidth={1.5} className="text-[var(--colony-text-tertiary)]" />;
      case "skipped": return <Minus size={12} strokeWidth={1.5} className="text-[var(--colony-text-tertiary)]" />;
      default: return null;
    }
  };

  const ciBgClass = (status: string) => {
    switch (status) {
      case "passed": return "bg-[rgba(60,203,127,0.08)]";
      case "failed": return "bg-[rgba(229,83,75,0.08)]";
      case "running": return "bg-[rgba(83,155,245,0.08)]";
      default: return "bg-[var(--colony-surface-elevated)]";
    }
  };

  const ciTextColor = (status: string) => {
    switch (status) {
      case "passed": return "var(--colony-green)";
      case "failed": return "var(--colony-red)";
      case "running": return "var(--colony-blue)";
      default: return "var(--colony-text-tertiary)";
    }
  };

  return (
    <div className="p-3 space-y-3">
      {/* Overall status */}
      <div className={cn("flex items-center gap-2 px-3 py-2.5 rounded-[8px] border border-[var(--colony-border)]", overallBg)}>
        {hasFail ? (
          <AlertTriangle size={14} strokeWidth={1.5} style={{ color: overallColor }} />
        ) : verificationState === "passed" ? (
          <CheckCircle size={14} strokeWidth={1.5} style={{ color: overallColor }} />
        ) : (
          <Clock size={14} strokeWidth={1.5} style={{ color: overallColor }} />
        )}
        <span className="text-[12px] font-medium" style={{ color: overallColor }}>{overallLabel}</span>
        {run.ciStatus.length > 0 && (
          <span className="text-[11px] text-[var(--colony-text-tertiary)] ml-auto">{passCount}/{run.ciStatus.length} passed</span>
        )}
      </div>

      {/* CI checks list */}
      {run.ciStatus.length > 0 ? (
        <div className="rounded-[8px] border border-[var(--colony-border)] bg-[var(--colony-surface)] overflow-hidden">
          <div className="px-3 py-2 border-b border-[var(--colony-border)]">
            <span className="text-[11px] font-medium text-[var(--colony-text-tertiary)] uppercase tracking-[0.05em]">Pipeline Checks</span>
          </div>
          <div className="divide-y divide-[var(--colony-border)]">
            {run.ciStatus.map((ci, i) => (
              <div key={i} className="flex items-center gap-2.5 px-3 py-2">
                <span className="shrink-0">{ciStatusIcon(ci.status)}</span>
                <span className="text-[12px] text-[var(--colony-text-primary)] flex-1">{ci.name}</span>
                {ci.duration && (
                  <span className="text-[10px] font-mono text-[var(--colony-text-tertiary)]">{ci.duration}</span>
                )}
                <span
                  className={cn("text-[10px] font-medium px-1.5 py-0.5 rounded-[3px] capitalize", ciBgClass(ci.status))}
                  style={{ color: ciTextColor(ci.status) }}
                >
                  {ci.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-[12px] text-[var(--colony-text-tertiary)]">No CI checks reported yet</div>
      )}

      {/* Test results */}
      {run.testResults && run.testResults.total > 0 && (
        <div className="rounded-[8px] border border-[var(--colony-border)] bg-[var(--colony-surface)] px-3 py-2.5">
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-[var(--colony-text-secondary)]">Tests:</span>
            <span className="text-[12px] font-mono" style={{ color: run.testResults.failed > 0 ? "var(--colony-red)" : "var(--colony-green)" }}>
              {run.testResults.passed}/{run.testResults.total} passed
            </span>
            {run.testResults.failed > 0 && (
              <span className="text-[12px] font-mono" style={{ color: "var(--colony-red)" }}>({run.testResults.failed} failed)</span>
            )}
          </div>
        </div>
      )}

      {/* Automated verification checks (machine-run build/typecheck/test) */}
      <AutomatedChecksSection run={run} />
    </div>
  );
}

// ── Risk Tab ────────────────────────────────────────────────────────

function RiskTab({ run }: { run: Run }) {
  const risk = riskConfig[run.riskLevel];
  const systemConfidence = computeSystemConfidence(run);

  const riskItems: { category: string; level: "low" | "medium" | "high"; detail: string }[] = [];

  // Build risk items from real data
  if (run.protectedFilesTouched.length > 0) {
    riskItems.push({ category: "Protected Files", level: "high", detail: `${run.protectedFilesTouched.length} protected file(s) modified: ${run.protectedFilesTouched.slice(0, 2).join(", ")}` });
  }
  if (run.hasMergeConflict) {
    riskItems.push({ category: "Merge Conflicts", level: "high", detail: `${run.mergeConflictFiles?.length || 0} conflicting file(s) detected` });
  }
  const totalChanges = run.filesChanged.reduce((s, f) => s + f.additions + f.deletions, 0);
  if (totalChanges > 500) {
    riskItems.push({ category: "Changeset Size", level: "medium", detail: `Large changeset with ${totalChanges} total changes across ${run.filesChanged.length} files` });
  } else if (totalChanges > 0) {
    riskItems.push({ category: "Changeset Size", level: "low", detail: `${totalChanges} changes across ${run.filesChanged.length} files` });
  }
  if (run.testResults && run.testResults.failed > 0) {
    riskItems.push({ category: "Test Coverage", level: "high", detail: `${run.testResults.failed} test(s) failing out of ${run.testResults.total}` });
  }
  if (run.failureSurface && run.failureSurface.length > 0) {
    riskItems.push(...run.failureSurface.slice(0, 2).map(f => ({
      category: "Potential Issue", level: "medium" as const, detail: f,
    })));
  }
  // Add confidence reasons as low-risk items if nothing else
  if (riskItems.length === 0) {
    systemConfidence.reasons.forEach(r => {
      riskItems.push({ category: r, level: "low", detail: "" });
    });
  }

  const riskColors = {
    low: { dot: "var(--colony-green)", badge: "bg-[rgba(60,203,127,0.1)]", text: "var(--colony-green)" },
    medium: { dot: "var(--colony-amber)", badge: "bg-[rgba(229,167,59,0.1)]", text: "var(--colony-amber)" },
    high: { dot: "var(--colony-red)", badge: "bg-[rgba(229,83,75,0.1)]", text: "var(--colony-red)" },
  };

  return (
    <div className="p-3 space-y-3">
      {/* Overall risk */}
      <div className={cn("flex items-center gap-2 px-3 py-2.5 rounded-[8px] border border-[var(--colony-border)]", risk.bgClass)}>
        <Shield size={14} strokeWidth={1.5} style={{ color: risk.colorVar }} />
        <span className="text-[12px] font-medium" style={{ color: risk.colorVar }}>
          Overall Risk: {risk.short}
        </span>
      </div>

      {/* Risk items */}
      {riskItems.length > 0 && (
        <div className="rounded-[8px] border border-[var(--colony-border)] bg-[var(--colony-surface)] overflow-hidden">
          <div className="px-3 py-2 border-b border-[var(--colony-border)]">
            <span className="text-[11px] font-medium text-[var(--colony-text-tertiary)] uppercase tracking-[0.05em]">Risk Assessment</span>
          </div>
          <div className="divide-y divide-[var(--colony-border)]">
            {riskItems.map((item, i) => (
              <div key={i} className="px-3 py-2.5">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: riskColors[item.level].dot }} />
                  <span className="text-[12px] font-medium text-[var(--colony-text-primary)] flex-1">{item.category}</span>
                  <span className={cn("text-[10px] font-medium px-1.5 py-0.5 rounded-[3px] capitalize", riskColors[item.level].badge)}
                    style={{ color: riskColors[item.level].text }}>
                    {item.level}
                  </span>
                </div>
                {item.detail && (
                  <p className="text-[11px] text-[var(--colony-text-tertiary)] leading-[1.5] pl-[14px]">{item.detail}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Merge conflict warning */}
      {run.hasMergeConflict && (
        <div className="p-3 rounded-[8px] border-l-[4px]" style={{ borderLeftColor: "var(--colony-red)", backgroundColor: "rgba(229,83,75,0.05)", borderColor: "rgba(229,83,75,0.15)" }}>
          <div className="flex items-center gap-2 mb-2 text-[12px] font-medium" style={{ color: "var(--colony-red)" }}>
            <AlertTriangle size={13} strokeWidth={1.5} />
            Merge Conflict Detected
          </div>
          {run.mergeConflictFiles && run.mergeConflictFiles.length > 0 && (
            <ul className="space-y-1 mb-2">
              {run.mergeConflictFiles.map((file, i) => (
                <li key={i} className="text-[11px] font-mono text-[var(--colony-text-tertiary)] truncate flex items-center gap-1.5 pl-[14px]">
                  <FileCode size={10} />{file}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────

interface EvidencePanelProps {
  run: Run;
  onClose: () => void;
}

export function EvidencePanel({ run, onClose }: EvidencePanelProps) {
  const { mergeRun, rejectRun, retryRun, startRun, stopRun } = useReviewStore();
  const completeOnboardingStep = useOnboardingStore((s) => s.completeStep);
  const [activeTab, setActiveTab] = useState<EvidenceTab>("details");
  const [scrolled, setScrolled] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [isFixingMergeConflict, setIsFixingMergeConflict] = useState(false);
  const [mergeSuccess, setMergeSuccess] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectFeedback, setRejectFeedback] = useState("");
  const [rejectAction, setRejectAction] = useState<"retry" | "delete" | "escalate" | null>(null);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectSuccess, setRejectSuccess] = useState(false);
  const [isStartingTask, setIsStartingTask] = useState(false);
  const [isStoppingTask, setIsStoppingTask] = useState(false);
  const [isResumingTask, setIsResumingTask] = useState(false);
  const [canResumeFromNode, setCanResumeFromNode] = useState<string | null>(null);
  const [showFullTrace, setShowFullTrace] = useState(false);

  const verificationState = getVerificationState(run);
  const mergeDisabledReason = getMergeDisabledReason(run, verificationState);
  const canMergeNow = !mergeDisabledReason;
  const isTaskRun = run.source === "task";
  const canStartTaskNow = isTaskRun && (run.taskPipelineStage === "backlog" || run.taskPipelineStage === "planning") && run.taskWorkflowStatus !== "queued" && run.taskWorkflowStatus !== "running";
  const canStopTaskNow = isTaskRun && (run.taskWorkflowStatus === "queued" || run.taskWorkflowStatus === "running" || run.taskPipelineStage === "in_progress");
  const canFixMergeWithAI = isTaskRun && Boolean(mergeError);
  const canResumeTask = isTaskRun && run.status === "blocked" && run.taskWorkflowStatus === "failed" && canResumeFromNode !== null;

  // Detect resumable failed node (local SQLite only)
  useEffect(() => {
    if (!isTaskRun || run.taskWorkflowStatus !== "failed") {
      setCanResumeFromNode(null);
      return;
    }
    if (!isTauri()) {
      setCanResumeFromNode(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const Database = (await import("@tauri-apps/plugin-sql")).default;
        const db = await Database.load("sqlite:factory-local.db");
        for (const table of ["workflow_runs", "local_workflow_runs"]) {
          try {
            const rows: any[] = await db.select(
              `SELECT current_node_id FROM ${table} WHERE task_id = ? AND status = 'failed' AND current_node_id IS NOT NULL ORDER BY started_at DESC LIMIT 1`,
              [run.id],
            );
            if (!cancelled && rows.length > 0 && rows[0].current_node_id) {
              setCanResumeFromNode(String(rows[0].current_node_id));
              return;
            }
          } catch { /* table may not exist */ }
        }
        if (!cancelled) setCanResumeFromNode(null);
      } catch {
        if (!cancelled) setCanResumeFromNode(null);
      }
    })();
    return () => { cancelled = true; };
  }, [run.id, run.taskWorkflowStatus, isTaskRun]);

  // ── Handlers ────────────────────────────────────────────────────

  const handleMerge = async () => { if (!canMergeNow) return; setMergeError(null); setShowMergeDialog(true); };

  const confirmMerge = async () => {
    setIsMerging(true);
    setMergeError(null);
    try {
      if (isTauri() && run.activeSessionId) {
        const session = useAiChatStore.getState().sessions.get(run.activeSessionId);
        if (session?.cwd) {
          try {
            const status = await tauriInvoke<{ files: { path: string; status: string }[] }>("git_status", { cwd: session.cwd });
            if (status.files && status.files.length > 0) {
              await tauriInvoke("git_add", { cwd: session.cwd, paths: ["."] });
              await tauriInvoke("git_commit", { cwd: session.cwd, message: run.title });
              await tauriInvoke("git_push", { cwd: session.cwd });
            }
          } catch (gitErr) { console.warn("Git operations skipped:", gitErr); }
        }
      }
      const merged = await mergeRun(run.id);
      if (!merged) throw new Error(useWorldStore.getState().saveError || "Merge failed. Please try again.");
      setIsMerging(false);
      setMergeSuccess(true);
      completeOnboardingStep("review_result");
      setTimeout(() => { setShowMergeDialog(false); setMergeSuccess(false); }, 1500);
    } catch (error) {
      setIsMerging(false);
      const rawMessage = error instanceof Error ? error.message : "Merge failed. Please try again.";
      setMergeError(getMergeErrorMessage(rawMessage));
    }
  };

  const handleResolveWithAI = async () => {
    if (!isTaskRun) return;
    setIsFixingMergeConflict(true);
    try {
      const fixed = await useWorldStore.getState().fixTaskMergeWithAI(run.id);
      if (!fixed) {
        throw new Error(useWorldStore.getState().saveError || "AI conflict resolution failed.");
      }
      setMergeError(null);
      setMergeSuccess(true);
      completeOnboardingStep("review_result");
      setTimeout(() => { setShowMergeDialog(false); setMergeSuccess(false); }, 1500);
    } catch (error) {
      const rawMessage = error instanceof Error ? error.message : "AI conflict resolution failed.";
      setMergeError(getMergeErrorMessage(rawMessage));
    } finally {
      setIsFixingMergeConflict(false);
    }
  };

  const handleReject = () => { setRejectFeedback(""); setRejectAction(null); setShowRejectDialog(true); };

  const confirmReject = async () => {
    if (!rejectAction) return;
    setIsRejecting(true);
    try {
      if (rejectAction === "retry" && run.activeSessionId && rejectFeedback.trim()) {
        const session = useAiChatStore.getState().sessions.get(run.activeSessionId);
        if (session?.state === "running") {
          await useAiChatStore.getState().sendMessage(run.activeSessionId, rejectFeedback);
        } else if (session) {
          await useAiChatStore.getState().createSession(session.provider, session.cwd, `Previous attempt feedback: ${rejectFeedback}\n\nOriginal task: ${run.title}`);
        }
      }
      await rejectRun(run.id, rejectFeedback || undefined);
      setIsRejecting(false);
      setRejectSuccess(true);
      completeOnboardingStep("review_result");
      setTimeout(() => { setShowRejectDialog(false); setRejectSuccess(false); setRejectAction(null); setRejectFeedback(""); }, 1500);
    } catch { setIsRejecting(false); }
  };

  const handleRetry = async () => {
    if (run.activeSessionId) {
      const session = useAiChatStore.getState().sessions.get(run.activeSessionId);
      if (session) {
        const prompt = session.messages.find(m => m.role === "user")?.content || run.title;
        try { await useAiChatStore.getState().createSession(session.provider, session.cwd, prompt); } catch (err) { console.error("Failed to retry:", err); }
      }
    }
    await retryRun(run.id);
  };

  const handleStartTask = async () => {
    setIsStartingTask(true);
    try {
      const success = await startRun(run.id);
      if (!success) window.alert(useWorldStore.getState().saveError ? `Failed to start task.\n\n${useWorldStore.getState().saveError}` : "Failed to start task.");
    } finally { setIsStartingTask(false); }
  };

  const handleStopTask = async () => {
    setIsStoppingTask(true);
    try { const success = await stopRun(run.id); if (!success) window.alert("Failed to stop task."); }
    finally { setIsStoppingTask(false); }
  };

  const handleResumeTask = async () => {
    setIsResumingTask(true);
    try {
      const success = await useWorldStore.getState().resumeTaskFromFailedNode(run.id);
      if (!success) {
        const err = useWorldStore.getState().saveError;
        console.error("[EvidencePanel] Resume failed:", err);
        window.alert(err || "Resume failed");
      } else {
        setCanResumeFromNode(null);
      }
    } finally {
      setIsResumingTask(false);
    }
  };

  const handleScroll = () => { if (contentRef.current) setScrolled(contentRef.current.scrollTop > 8); };

  // ── Tab bar ─────────────────────────────────────────────────────

  const hasAutomatedChecks = mapEvidenceToChecks(run.nodeResults ?? []).length > 0;
  const tabs: { id: EvidenceTab; label: string }[] = [
    { id: "details", label: "Details" },
    { id: "context", label: "Context" },
    { id: "changes", label: "Changes" },
    ...(run.ciStatus.length > 0 || hasAutomatedChecks ? [{ id: "ci" as const, label: "CI" }] : []),
    { id: "risk", label: "Risk" },
  ];

  const renderTab = () => {
    switch (activeTab) {
      case "details": return <DetailsTab run={run} onTabChange={setActiveTab} />;
      case "context": return <ContextTab run={run} />;
      case "changes": return <ChangesTab run={run} />;
      case "ci": return <CITab run={run} />;
      case "risk": return <RiskTab run={run} />;
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden" data-testid="panel-evidence">
      {/* Header with tabs */}
      <div
        className="shrink-0 px-4 pt-3 pb-0 transition-shadow duration-200"
        style={{ boxShadow: scrolled ? "0 4px 16px rgba(0,0,0,0.25), 0 1px 4px rgba(0,0,0,0.15)" : "none" }}
      >
        <h3 className="text-[14px] font-semibold text-[var(--colony-text-heading)] tracking-[-0.015em] mb-2.5 px-0.5">Evidence</h3>
        <div className="flex items-center border-b border-[var(--colony-border)]">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex-1 text-center h-8 text-[12px] font-medium transition-all duration-150 border-b-2 -mb-px cursor-pointer",
                activeTab === tab.id
                  ? "text-[var(--colony-text-primary)] border-[var(--colony-brand)]"
                  : "text-[var(--colony-text-tertiary)] border-transparent hover:text-[var(--colony-text-secondary)]"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div
        ref={contentRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto"
        style={{ scrollbarWidth: "thin", scrollbarColor: "#2A2A2F transparent" }}
      >
        {renderTab()}

        {/* Full trace (always available at bottom of any tab) */}
        {run.aiActivity && activeTab === "details" && (
          <div className="px-4 pb-4">
            <button
              onClick={() => setShowFullTrace(!showFullTrace)}
              className="w-full px-3 py-2 rounded-[6px] border border-[var(--colony-border)] bg-[var(--colony-surface)] flex items-center justify-between hover:bg-[var(--colony-surface-hover)] transition-colors text-[var(--colony-text-tertiary)]"
            >
              <div className="flex items-center gap-2">
                <Terminal size={12} strokeWidth={1.5} />
                <span className="text-[11px]">View Full Trace</span>
              </div>
              {showFullTrace ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>
            {showFullTrace && (
              <div className="mt-2">
                <AIActivityLog activity={run.aiActivity} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="shrink-0 border-t border-[var(--colony-border)] px-3 py-3 space-y-2">
        {canStartTaskNow && (
          <Button className="w-full gap-2 h-8 text-[12px]" onClick={handleStartTask} disabled={isStartingTask}>
            {isStartingTask ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
            {isStartingTask ? "Starting..." : "Start Task"}
          </Button>
        )}

        {canStopTaskNow && (
          <Button variant="outline" className="w-full gap-2 h-8 text-[12px]" onClick={handleStopTask} disabled={isStoppingTask}>
            {isStoppingTask ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
            {isStoppingTask ? "Stopping..." : "Stop Task"}
          </Button>
        )}

        {run.status === "blocked" && (
          <div className="space-y-2">
            {canResumeTask && (
              <Button className="w-full gap-2 h-8 text-[12px]" onClick={handleResumeTask} disabled={isResumingTask}>
                {isResumingTask ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <SkipForward className="w-3.5 h-3.5" />}
                {isResumingTask ? "Resuming..." : `Resume from ${canResumeFromNode}`}
              </Button>
            )}
            <Button variant={canResumeTask ? "outline" : "default"} className="w-full gap-2 h-8 text-[12px]" onClick={handleRetry}>
              <RefreshCw className="w-3.5 h-3.5" />Retry from Start
            </Button>
          </div>
        )}

        {(run.status === "needs_review" || run.status === "ready") && (
          <>
            <div className="flex items-center gap-2">
              <button
                className="flex-1 flex items-center justify-center gap-1.5 h-8 rounded-[6px] bg-[var(--colony-surface-elevated)] border border-[var(--colony-border)] text-[var(--colony-text-primary)] text-[12px] font-medium hover:bg-[var(--colony-surface-hover)] transition-all duration-150 cursor-pointer"
                onClick={handleReject}
              >
                <XCircle size={13} strokeWidth={1.5} className="text-[var(--colony-text-secondary)]" />
                Reject
                <span className="text-[10px] text-[var(--colony-text-tertiary)] font-mono ml-1">&#8984;&#9003;</span>
              </button>
              <button
                className={cn(
                  "flex-1 flex items-center justify-center gap-1.5 h-8 rounded-[6px] text-[12px] font-medium transition-all duration-150",
                  !canMergeNow
                    ? "bg-[var(--colony-surface-elevated)] border border-[var(--colony-border)] text-[var(--colony-text-tertiary)] cursor-not-allowed opacity-60"
                    : "bg-[#01B19D] text-white cursor-pointer hover:bg-[#00a08e]"
                )}
                onClick={handleMerge}
                disabled={!canMergeNow}
                title={mergeDisabledReason}
              >
                <GitMerge size={13} strokeWidth={1.5} />
                {run.hasMergeConflict ? "Conflict" : canMergeNow ? "Merge" : "Merge"}
                <span className="text-[10px] font-mono ml-1 opacity-70">&#8984;&#9166;</span>
              </button>
            </div>
            {!canMergeNow && mergeDisabledReason && (
              <p className="text-[11px] text-[var(--colony-text-tertiary)] text-center">{mergeDisabledReason}</p>
            )}
            {canMergeNow && verificationState === "not_run" && (
              <p className="text-[11px] text-[var(--colony-amber)] text-center">No CI checks configured — merging without verification</p>
            )}
            {canMergeNow && verificationState === "running" && (
              <p className="text-[11px] text-[var(--colony-amber)] text-center">CI checks still running</p>
            )}
          </>
        )}

        {run.status === "merged" && (
          <div className="text-center py-1">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#01B19D]/10 border border-[#01B19D]/20">
              <CheckCircle className="w-3.5 h-3.5 text-[#01B19D]" />
              <span className="text-[12px] font-medium text-[#01B19D]">Merged successfully</span>
            </div>
          </div>
        )}

        {/* Open in IDE */}
        <button
          className="w-full flex items-center justify-center gap-1.5 h-7 text-[12px] text-[var(--colony-text-secondary)] hover:text-[var(--colony-text-primary)] transition-colors cursor-pointer"
          data-testid="button-open-ide"
          onClick={async () => {
            if (isTauri() && run.activeSessionId) {
              const session = useAiChatStore.getState().sessions.get(run.activeSessionId);
              if (session?.cwd) { try { await tauriInvoke("pty_create", { cwd: session.cwd }); } catch (err) { console.error("Failed to open terminal:", err); } }
            }
          }}
        >
          <SquareTerminal size={13} strokeWidth={1.5} />
          Open in IDE
          <span className="text-[10px] text-[var(--colony-text-tertiary)] font-mono ml-1">&#8984;O</span>
        </button>
      </div>

      {/* MERGE CONFIRMATION DIALOG */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent className="sm:max-w-md bg-card border-border/60">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {mergeSuccess ? (
                <><CheckCircle className="w-5 h-5 text-emerald-400" /><span className="text-emerald-400">Merge Successful</span></>
              ) : (
                <><GitMerge className="w-5 h-5 text-primary" /><span>Confirm Merge</span></>
              )}
            </DialogTitle>
            {!mergeSuccess && (
              <DialogDescription>You are about to merge {run.prNumber ? `PR #${run.prNumber}` : "this branch"} into the main branch. This action cannot be undone.</DialogDescription>
            )}
          </DialogHeader>

          {isMerging ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-muted animate-pulse" />
                <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
              </div>
              <div className="text-center">
                <p className="font-medium text-foreground">Merging changes...</p>
                <p className="text-sm text-muted-foreground mt-1">This may take a few seconds</p>
              </div>
            </div>
          ) : mergeSuccess ? (
            <div className="flex flex-col items-center justify-center py-6 space-y-3">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <p className="text-sm text-muted-foreground">Changes have been merged successfully</p>
            </div>
          ) : mergeError ? (
            <div className="flex flex-col items-center justify-center py-6 space-y-4">
              <div className="w-16 h-16 rounded-full bg-rose-500/10 border border-rose-500/20 flex items-center justify-center">
                <XCircle className="w-8 h-8 text-rose-400" />
              </div>
              <div className="text-center">
                <p className="font-medium text-rose-400 mb-1">Merge Failed</p>
                <p className="text-sm text-muted-foreground">{mergeError}</p>
              </div>
              <div className="flex gap-2 mt-2">
                <Button variant="outline" size="sm" onClick={() => setMergeError(null)}>Try Again</Button>
                {canFixMergeWithAI && (
                  <Button
                    size="sm"
                    onClick={handleResolveWithAI}
                    disabled={isFixingMergeConflict}
                    className="gap-1.5 bg-[#01B19D] hover:bg-[#00a08e] text-white border-0 ring-0 outline-none focus:ring-0 focus-visible:ring-0"
                  >
                    {isFixingMergeConflict ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {isFixingMergeConflict ? "Fixing..." : "Fix with AI"}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-400 mb-1">Merge Warning</p>
                    <p className="text-sm text-amber-300/80">Please ensure all CI checks have passed and the code has been properly reviewed.</p>
                  </div>
                </div>
              </div>
              <div className="text-sm text-muted-foreground">
                <div className="flex justify-between py-2 border-b border-border/30"><span>Branch</span><span className="font-mono text-foreground/80">{run.branch || "auto-detected"}</span></div>
                <div className="flex justify-between py-2 border-b border-border/30"><span>Files changed</span><span className="text-foreground/80">{run.filesChanged.length}</span></div>
                <div className="flex justify-between py-2"><span>Risk level</span><span className="font-medium" style={{ color: riskConfig[run.riskLevel].colorVar }}>{riskConfig[run.riskLevel].label}</span></div>
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button variant="outline" onClick={() => setShowMergeDialog(false)} className="flex-1">Cancel</Button>
                <Button onClick={confirmMerge} disabled={isMerging} className="flex-1 gap-2 bg-[#01B19D] hover:bg-[#00a08e] text-white border-0 ring-0 outline-none focus:ring-0 focus-visible:ring-0">
                  <GitMerge className="w-4 h-4" />Confirm Merge
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* REJECT DIALOG */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="sm:max-w-lg bg-card border-border/60">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {rejectSuccess ? (
                <><CheckCircle className="w-5 h-5 text-emerald-400" /><span className="text-emerald-400">Done</span></>
              ) : (
                <><XCircle className="w-5 h-5 text-rose-400" /><span>Reject Changes</span></>
              )}
            </DialogTitle>
            {!rejectSuccess && <DialogDescription>Choose what to do with this work and provide feedback if needed.</DialogDescription>}
          </DialogHeader>

          {isRejecting ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-muted animate-pulse" />
                <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-8 h-8 text-rose-400 animate-spin" /></div>
              </div>
              <p className="font-medium text-foreground">
                {rejectAction === "retry" && "Sending feedback to agent..."}
                {rejectAction === "delete" && "Deleting branch..."}
                {rejectAction === "escalate" && "Escalating to team..."}
              </p>
            </div>
          ) : rejectSuccess ? (
            <div className="flex flex-col items-center justify-center py-6 space-y-3">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <p className="text-sm text-muted-foreground">
                {rejectAction === "retry" && "Agent will retry with your feedback"}
                {rejectAction === "delete" && "Branch deleted successfully"}
                {rejectAction === "escalate" && "Escalated to your team"}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground mb-3">What would you like to do?</p>
                {([
                  { action: "retry" as const, icon: RotateCcw, label: "Retry with Feedback", desc: "Send feedback to the agent so it can try again with your guidance", color: "amber" },
                  { action: "delete" as const, icon: Trash2, label: "Delete Branch", desc: "Permanently remove this branch and all its changes", color: "rose" },
                  { action: "escalate" as const, icon: UserCheck, label: "Escalate to Team", desc: "Request a human developer to review and handle this", color: "primary" },
                ]).map(opt => (
                  <button key={opt.action} onClick={() => setRejectAction(opt.action)}
                    className={cn("w-full p-4 rounded-lg border text-left transition-all",
                      rejectAction === opt.action ? `border-${opt.color}-500/50 bg-${opt.color}-500/10` : "border-border/50 bg-muted/20 hover:bg-muted/40")}>
                    <div className="flex items-start gap-3">
                      <opt.icon className={`w-5 h-5 text-${opt.color}-400 mt-0.5`} />
                      <div>
                        <p className="font-medium text-foreground">{opt.label}</p>
                        <p className="text-sm text-muted-foreground mt-0.5">{opt.desc}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              {rejectAction === "retry" && (
                <div className="mt-4">
                  <label className="text-sm font-medium text-foreground mb-2 block">Feedback for the agent</label>
                  <textarea value={rejectFeedback} onChange={(e) => setRejectFeedback(e.target.value)}
                    placeholder="Explain what needs to change..."
                    className="w-full h-24 px-3 py-2 text-sm rounded-lg border border-border/50 bg-muted/20 resize-none focus:outline-none focus:ring-2 focus:ring-primary/50" />
                </div>
              )}
              <DialogFooter className="gap-2 sm:gap-2 mt-4">
                <Button variant="outline" onClick={() => setShowRejectDialog(false)} className="flex-1">Cancel</Button>
                <Button onClick={confirmReject} disabled={!rejectAction || (rejectAction === "retry" && !rejectFeedback.trim())}
                  className={cn("flex-1 gap-2", rejectAction === "delete"
                    ? "bg-gradient-to-b from-rose-500 to-rose-600 hover:from-rose-400 hover:to-rose-500 text-white"
                    : "bg-gradient-to-b from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white")}>
                  {rejectAction === "retry" && <><RotateCcw className="w-4 h-4" />Send &amp; Retry</>}
                  {rejectAction === "delete" && <><Trash2 className="w-4 h-4" />Delete Branch</>}
                  {rejectAction === "escalate" && <><UserCheck className="w-4 h-4" />Escalate</>}
                  {!rejectAction && "Select an option"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
